import React from 'react'
import { Box, Chip, MenuItem, Select, Typography, Stack } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ENVIRONMENTS } from 'src/config/environments'
import { useEnvironmentStore } from 'src/store/useEnvironmentStore'

// -----------------------------------------------------------------------

/**
 * Dropdown selector that allows the admin to switch the target backend.
 * The selected environment is persisted in localStorage so it survives
 * page reloads. Authentication (Entra ID) is NOT affected by this switch.
 */
export default function EnvironmentSelector() {
  const selectedEnv = useEnvironmentStore((state) => state.selectedEnv)
  const setSelectedEnv = useEnvironmentStore((state) => state.setSelectedEnv)
  const currentEnv = ENVIRONMENTS[selectedEnv]

  const handleChange = (event) => {
    const newEnv = event.target.value
    // setSelectedEnv rewrites ?env= in the URL and reloads, so MSAL (auth realm)
    // and axios (base URL) re-resolve cleanly for the new environment.
    setSelectedEnv(newEnv)
  }

  return (
    <Select
      value={selectedEnv}
      onChange={handleChange}
      size="small"
      variant="outlined"
      renderValue={(value) => {
        const env = ENVIRONMENTS[value]
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: env?.color,
              }}
            />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
              {env?.label}
            </Typography>
          </Stack>
        )
      }}
      sx={{
        minWidth: 150,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: (theme) => alpha(currentEnv?.color || theme.palette.primary.main, 0.5),
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: currentEnv?.color,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: currentEnv?.color,
        },
      }}
    >
      {Object.entries(ENVIRONMENTS).map(([key, env]) => (
        <MenuItem key={key} value={key}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: env.color,
                flexShrink: 0,
              }}
            />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {env.label}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {env.baseUrl}
              </Typography>
            </Box>
            {key === selectedEnv && (
              <Chip label="Active" size="small" sx={{ backgroundColor: alpha(env.color, 0.15), color: env.color, fontWeight: 600, fontSize: '0.7rem' }} />
            )}
          </Stack>
        </MenuItem>
      ))}
    </Select>
  )
}
